import { z } from 'zod';
import { CalendarAbsenceEntrySchema } from './absence';

/**
 * Phase 2a M1: holidays (spec docs/specs/phase-2-holidays.md, D31–D37). Aroo stores facts
 * (allocation, adjustments, bookings with their history) and derives the balance in one place
 * (apps/api/src/holidays/balance.ts); these are the shapes the API serves and the web renders.
 * Dates are YYYY-MM-DD; days are decimals in halves.
 */

const IsoDate = z.iso.date();
const Days = z.number().multipleOf(0.5);

export const HolidayBookingStatusSchema = z.enum([
  'pending',
  'approved',
  'declined',
  'cancelled',
  'withdrawn',
]);
export type HolidayBookingStatus = z.infer<typeof HolidayBookingStatusSchema>;

export const HolidayBookingKindSchema = z.enum(['holiday', 'bank_holiday_auto']);
export type HolidayBookingKind = z.infer<typeof HolidayBookingKindSchema>;

export const HolidayHalfSchema = z.enum(['am', 'pm']);
export type HolidayHalf = z.infer<typeof HolidayHalfSchema>;

export const HolidayAdjustmentKindSchema = z.enum([
  'carry_over',
  'correction',
  'lieu',
  'pro_rata',
  'other',
]);
export type HolidayAdjustmentKind = z.infer<typeof HolidayAdjustmentKindSchema>;

export const HOLIDAY_ADJUSTMENT_LABELS: Record<HolidayAdjustmentKind, string> = {
  carry_over: 'Carry-over',
  correction: 'Correction',
  lieu: 'Day in lieu',
  pro_rata: 'Pro-rata',
  other: 'Adjustment',
};

export const HOLIDAY_STATUS_LABELS: Record<HolidayBookingStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
  withdrawn: 'Withdrawn',
};

// Working pattern ---------------------------------------------------------------

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

/** Which days of the week count as working days for a person. */
export const WeeklyPatternSchema = z.object({
  mon: z.boolean(),
  tue: z.boolean(),
  wed: z.boolean(),
  thu: z.boolean(),
  fri: z.boolean(),
  sat: z.boolean(),
  sun: z.boolean(),
});
export type WeeklyPattern = z.infer<typeof WeeklyPatternSchema>;

/** Wall-clock time of day, 24-hour "HH:MM" (Europe/London). */
export const HhMmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');

/**
 * Phase 3 (D51): a working day's shift times and unpaid break. All null = the day is a
 * working day with no times recorded ("due but untimed"); the engine then checks presence only.
 */
export const DayTimesSchema = z.object({
  start: HhMmSchema.nullable(),
  finish: HhMmSchema.nullable(),
  breakMinutes: z.number().int().min(0).max(600).nullable(),
});
export type DayTimes = z.infer<typeof DayTimesSchema>;

/** Times per weekday for one week of a pattern (days off keep whatever is stored; ignored). */
export const WeekTimesSchema = z.object({
  mon: DayTimesSchema,
  tue: DayTimesSchema,
  wed: DayTimesSchema,
  thu: DayTimesSchema,
  fri: DayTimesSchema,
  sat: DayTimesSchema,
  sun: DayTimesSchema,
});
export type WeekTimes = z.infer<typeof WeekTimesSchema>;

export const NO_DAY_TIMES: DayTimes = { start: null, finish: null, breakMinutes: null };
export const NO_WEEK_TIMES: WeekTimes = {
  mon: NO_DAY_TIMES,
  tue: NO_DAY_TIMES,
  wed: NO_DAY_TIMES,
  thu: NO_DAY_TIMES,
  fri: NO_DAY_TIMES,
  sat: NO_DAY_TIMES,
  sun: NO_DAY_TIMES,
};

/** Where a stored pattern came from: the old shift grid, the old rota pattern, the site's trading days, or HR. */
export const WorkingPatternSourceSchema = z.enum([
  'shift_grid',
  'rota_pattern',
  'site_default',
  'manual',
]);
export type WorkingPatternSource = z.infer<typeof WorkingPatternSourceSchema>;

/** M2b (D39): a pattern is one week, or two weeks alternating from a Monday anchor. */
export const CycleWeeksSchema = z.union([z.literal(1), z.literal(2)]);
export type CycleWeeks = z.infer<typeof CycleWeeksSchema>;

export const WorkingPatternSchema = z.object({
  id: z.uuid(),
  effectiveFrom: IsoDate,
  /** Week 1 (the only week of a one-week pattern). */
  days: WeeklyPatternSchema,
  cycleWeeks: CycleWeeksSchema,
  /** Week 2 of a two-week pattern; null for a one-week pattern. */
  week2: WeeklyPatternSchema.nullable(),
  /** The Monday that starts a week 1; null for a one-week pattern. */
  anchor: IsoDate.nullable(),
  /** A manager confirmed on the Saturday rota that this person works every Saturday. */
  confirmedEverySaturday: z.boolean(),
  source: WorkingPatternSourceSchema,
  /** Old-system reference the import keyed on (rota name, grid id); null for manual rows. */
  legacyRef: z.string().nullable(),
  setBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  setAt: z.iso.datetime(),
  /** M3: HR's note on why the pattern changed (null for imported and rota-set rows). */
  note: z.string().nullable(),
  /** Phase 3: shift times and breaks for week 1 (and week 2 of a two-week pattern). */
  times: WeekTimesSchema,
  week2Times: WeekTimesSchema.nullable(),
});
export type WorkingPattern = z.infer<typeof WorkingPatternSchema>;

export const OverrideReasonSchema = z.enum(['swap', 'cover', 'one_off', 'other']);
export type OverrideReason = z.infer<typeof OverrideReasonSchema>;

export const OVERRIDE_REASON_LABELS: Record<OverrideReason, string> = {
  swap: 'Swap',
  cover: 'Covering',
  one_off: 'One-off',
  other: 'Other',
};

/** M2b (D40): one date where the pattern does not apply (a swap, cover, a one-off). */
export const WorkingDayOverrideSchema = z.object({
  id: z.uuid(),
  personId: z.uuid(),
  date: IsoDate,
  works: z.boolean(),
  reason: OverrideReasonSchema,
  swappedWith: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  setBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  createdAt: z.iso.datetime(),
});
export type WorkingDayOverride = z.infer<typeof WorkingDayOverrideSchema>;

export const WorkingPatternResponseSchema = z.object({
  personId: z.uuid(),
  /** Every pattern on record, newest effective date first; the first one is in force today. */
  patterns: z.array(WorkingPatternSchema),
  /** The week pattern in force today (the site default when nothing is recorded). */
  current: WeeklyPatternSchema,
  currentSource: WorkingPatternSourceSchema,
  /** Dated exceptions, newest first (the last year and everything upcoming). */
  overrides: z.array(WorkingDayOverrideSchema),
  /** M3: bookings whose day count moved because a PUT changed the pattern (empty on a GET). */
  recounted: z.array(z.object({ bookingId: z.uuid(), personId: z.uuid(), from: Days, to: Days })),
});
export type WorkingPatternResponse = z.infer<typeof WorkingPatternResponseSchema>;

export const WorkingPatternPutSchema = z.object({
  effectiveFrom: IsoDate,
  days: WeeklyPatternSchema,
  /** Omitted = a one-week pattern. */
  cycleWeeks: CycleWeeksSchema.optional(),
  week2: WeeklyPatternSchema.nullable().optional(),
  /** Any date in a week 1; stored as that week's Monday. Required with cycleWeeks = 2. */
  anchor: IsoDate.nullable().optional(),
  /** M3: why (HR's full pattern editor). */
  note: z.string().trim().max(500).nullable().optional(),
  /**
   * Phase 3: shift times and breaks. Omitted = keep the times of the pattern in force on
   * `effectiveFrom` (so a days-only edit never loses them).
   */
  times: WeekTimesSchema.optional(),
  week2Times: WeekTimesSchema.nullable().optional(),
});
export type WorkingPatternPut = z.infer<typeof WorkingPatternPutSchema>;

/**
 * Everything a screen needs to say whether a date is a working day for the person, the way
 * the API counts it (apps/api/src/holidays/pattern.ts): the dated patterns in force, their
 * cycles, the overrides, and the site default when no pattern is recorded.
 */
export const WorkingDaysPatternSchema = z.object({
  effectiveFrom: IsoDate,
  cycleWeeks: CycleWeeksSchema,
  anchor: IsoDate.nullable(),
  week1: WeeklyPatternSchema,
  week2: WeeklyPatternSchema.nullable(),
});
export type WorkingDaysPattern = z.infer<typeof WorkingDaysPatternSchema>;

export const WorkingDaysModelSchema = z.object({
  fallback: WeeklyPatternSchema,
  patterns: z.array(WorkingDaysPatternSchema),
  overrides: z.array(z.object({ date: IsoDate, works: z.boolean() })),
});
export type WorkingDaysModel = z.infer<typeof WorkingDaysModelSchema>;

// Overrides and swaps (M2b, D40) -------------------------------------------------

export const OverrideCreateSchema = z.object({
  date: IsoDate,
  works: z.boolean(),
  reason: OverrideReasonSchema,
  /** Required for a swap: the colleague who takes the opposite side on the same date. */
  swappedWithPersonId: z.uuid().optional(),
});
export type OverrideCreate = z.infer<typeof OverrideCreateSchema>;

/** A booking whose day count moved because a working day changed. */
export const RecountedBookingSchema = z.object({
  bookingId: z.uuid(),
  personId: z.uuid(),
  from: Days,
  to: Days,
});
export type RecountedBooking = z.infer<typeof RecountedBookingSchema>;

export const OverrideResponseSchema = z.object({
  overrides: z.array(WorkingDayOverrideSchema),
  recounted: z.array(RecountedBookingSchema),
});
export type OverrideResponse = z.infer<typeof OverrideResponseSchema>;

export const OverrideDeleteResponseSchema = z.object({
  removed: z.array(z.uuid()),
  recounted: z.array(RecountedBookingSchema),
});
export type OverrideDeleteResponse = z.infer<typeof OverrideDeleteResponseSchema>;

// Saturday rota (M2b, D39) ----------------------------------------------------------

/** Which Saturdays a person works: every one, week 1 only, week 2 only, or not yet set. */
export const SaturdayStateSchema = z.enum(['every', 'week1', 'week2', 'unset']);
export type SaturdayState = z.infer<typeof SaturdayStateSchema>;

export const SATURDAY_STATE_LABELS: Record<SaturdayState, string> = {
  every: 'Every Saturday',
  week1: 'Week 1 Saturdays',
  week2: 'Week 2 Saturdays',
  unset: 'Not set',
};

export const RotaPersonSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  initials: z.string(),
  jobTitle: z.string().nullable(),
  state: SaturdayStateSchema,
  /** Where the state comes from: an imported row, HR, the site default. */
  source: WorkingPatternSourceSchema,
});
export type RotaPerson = z.infer<typeof RotaPersonSchema>;

export const RotaSaturdaySchema = z.object({
  date: IsoDate,
  /** Which week of the site's cycle the Saturday falls in. */
  week: CycleWeeksSchema,
});
export type RotaSaturday = z.infer<typeof RotaSaturdaySchema>;

/** GET /holidays/sites/:code/saturday-rota */
export const SaturdayRotaResponseSchema = z.object({
  site: z.object({ code: z.string(), name: z.string() }),
  /** The Monday that starts week 1 at this site (defaulted, not yet stored, when `anchorStored` is false). */
  anchor: IsoDate,
  anchorStored: z.boolean(),
  today: IsoDate,
  /** The next six Saturdays from today, with their cycle week. */
  saturdays: z.array(RotaSaturdaySchema),
  people: z.array(RotaPersonSchema),
  /** Overrides on those Saturdays, so a preview can show swaps. */
  overrides: z.array(WorkingDayOverrideSchema),
});
export type SaturdayRotaResponse = z.infer<typeof SaturdayRotaResponseSchema>;

export const SaturdayRotaPutSchema = z.object({
  people: z
    .array(
      z.object({
        personId: z.uuid(),
        state: z.enum(['every', 'week1', 'week2']),
      }),
    )
    .max(200),
});
export type SaturdayRotaPut = z.infer<typeof SaturdayRotaPutSchema>;

export const RotaChangeSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  from: SaturdayStateSchema,
  to: SaturdayStateSchema,
});
export type RotaChange = z.infer<typeof RotaChangeSchema>;

export const SaturdayRotaSaveResponseSchema = z.object({
  rota: SaturdayRotaResponseSchema,
  changes: z.array(RotaChangeSchema),
  recounted: z.array(RecountedBookingSchema),
});
export type SaturdayRotaSaveResponse = z.infer<typeof SaturdayRotaSaveResponseSchema>;

// Stale pending (M2b) -----------------------------------------------------------------

/** POST /holidays/admin/stale-pending/withdraw */
export const StaleWithdrawResponseSchema = z.object({
  withdrawn: z.number().int().min(0),
  bookingIds: z.array(z.uuid()),
});
export type StaleWithdrawResponse = z.infer<typeof StaleWithdrawResponseSchema>;

// Balance ------------------------------------------------------------------------

/** One line of the visible arithmetic: "28 − 3 adjustments − 11 taken − 4 booked − 2 pending = 8". */
export const BalanceLineSchema = z.object({
  kind: z.enum(['allocation', 'adjustment', 'taken', 'booked', 'pending']),
  label: z.string(),
  /** Signed: allocation and positive adjustments add, taken/booked/pending subtract. */
  days: z.number(),
});
export type BalanceLine = z.infer<typeof BalanceLineSchema>;

export const HolidayBalanceSchema = z.object({
  year: z.number().int(),
  allocation: Days,
  adjustments: z.number(),
  /** allocation + Σ adjustments */
  entitled: z.number(),
  /** Approved and auto bookings that ended before today. */
  taken: z.number(),
  /** Approved and auto bookings ending today or later. */
  booked: z.number(),
  pending: z.number(),
  /** entitled − taken − booked − pending (may be negative after an HR correction). */
  remaining: z.number(),
  lines: z.array(BalanceLineSchema),
});
export type HolidayBalance = z.infer<typeof HolidayBalanceSchema>;

// Bookings -----------------------------------------------------------------------

export const HolidayBookingEventSchema = z.object({
  from: HolidayBookingStatusSchema.nullable(),
  to: HolidayBookingStatusSchema,
  by: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  at: z.iso.datetime(),
  note: z.string().nullable(),
});
export type HolidayBookingEvent = z.infer<typeof HolidayBookingEventSchema>;

export const HolidayBookingSchema = z.object({
  id: z.uuid(),
  startDate: IsoDate,
  endDate: IsoDate,
  half: HolidayHalfSchema.nullable(),
  days: Days,
  kind: HolidayBookingKindSchema,
  status: HolidayBookingStatusSchema,
  note: z.string().nullable(),
  requestedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  requestedAt: z.iso.datetime(),
  decidedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelReason: z.string().nullable(),
  /** Bank holiday name for `bank_holiday_auto` bookings. */
  bankHolidayName: z.string().nullable(),
  /** Set on rows the import brought over from the old system. */
  imported: z.boolean(),
  events: z.array(HolidayBookingEventSchema),
});
export type HolidayBooking = z.infer<typeof HolidayBookingSchema>;

export const HolidayAdjustmentSchema = z.object({
  id: z.uuid(),
  days: z.number(),
  kind: HolidayAdjustmentKindSchema,
  reason: z.string(),
  madeBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  madeAt: z.iso.datetime(),
  /** M3: a reversed adjustment stays on record (audit) but no longer counts. */
  reversedAt: z.iso.datetime().nullable(),
  reversedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export type HolidayAdjustment = z.infer<typeof HolidayAdjustmentSchema>;

export const BankHolidaySchema = z.object({
  date: IsoDate,
  name: z.string(),
});
export type BankHoliday = z.infer<typeof BankHolidaySchema>;

export const BankHolidaysResponseSchema = z.object({
  year: z.number().int(),
  region: z.string(),
  bankHolidays: z.array(BankHolidaySchema),
  /** When the gov.uk feed was last refreshed; null if only the shipped fixture has been loaded. */
  publishedAt: z.iso.datetime().nullable(),
});
export type BankHolidaysResponse = z.infer<typeof BankHolidaysResponseSchema>;

/** GET /holidays/me and GET /holidays/people/:id: one person's holiday year. */
export const HolidaySummarySchema = z.object({
  person: z.object({ id: z.uuid(), name: z.string(), firstName: z.string() }),
  year: z.number().int(),
  /** Years this person has an allocation or bookings in, for the switcher. */
  years: z.array(z.number().int()),
  /** Null when HR has not set an allocation for this year yet. */
  allocation: z
    .object({
      days: Days,
      bankHolidaysBooked: z.boolean(),
      casual: z.boolean(),
      setAt: z.iso.datetime(),
      setBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    })
    .nullable(),
  balance: HolidayBalanceSchema,
  bookings: z.array(HolidayBookingSchema),
  adjustments: z.array(HolidayAdjustmentSchema),
  /** The week pattern in force today (labels only; `workingDays` is what counts a date). */
  pattern: WeeklyPatternSchema,
  patternSource: WorkingPatternSourceSchema,
  /** M2b: the full model (two-week cycles, overrides) for the live day-count preview. */
  workingDays: WorkingDaysModelSchema,
  bankHolidays: z.array(BankHolidaySchema),
  /** The day the balance was computed for. */
  today: IsoDate,
});
export type HolidaySummary = z.infer<typeof HolidaySummarySchema>;

export const HolidayYearQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export type HolidayYearQuery = z.infer<typeof HolidayYearQuerySchema>;

// Branch calendar ------------------------------------------------------------------

/** M3: a site or a region (one of the two), a month, optionally one job title. */
export const HolidayCalendarQuerySchema = z
  .object({
    /** Site code. */
    site: z.string().trim().min(1).max(32).optional(),
    /** Region code (M3): every open branch in the region. */
    region: z.string().trim().min(1).max(32).optional(),
    /** YYYY-MM */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    /** Job title filter, case-insensitive ("Driver"). */
    role: z.string().trim().max(100).optional(),
  })
  .refine((q) => (q.site ? 1 : 0) + (q.region ? 1 : 0) === 1, {
    message: 'Pass a site or a region',
    path: ['site'],
  });
export type HolidayCalendarQuery = z.infer<typeof HolidayCalendarQuerySchema>;

export const CalendarEntrySchema = z.object({
  bookingId: z.uuid(),
  personId: z.uuid(),
  name: z.string(),
  startDate: IsoDate,
  endDate: IsoDate,
  half: HolidayHalfSchema.nullable(),
  status: z.enum(['pending', 'approved']),
  kind: HolidayBookingKindSchema,
});
export type CalendarEntry = z.infer<typeof CalendarEntrySchema>;

export const CalendarPersonSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  initials: z.string(),
  /** Null in the staff (names-only) variant. */
  jobTitle: z.string().nullable(),
  siteCode: z.string(),
});
export type CalendarPerson = z.infer<typeof CalendarPersonSchema>;

export const CalendarDaySchema = z.object({
  date: IsoDate,
  /** "Mon" */
  weekday: z.string(),
  weekend: z.boolean(),
  bankHoliday: z.string().nullable(),
  /** M3 placeholder: a site closure on the day (none recorded yet). */
  closure: z.string().nullable(),
});
export type CalendarDay = z.infer<typeof CalendarDaySchema>;

export const HolidayCalendarResponseSchema = z.object({
  /** The first (or only) site, kept for screens that read one site. */
  site: z.object({ code: z.string(), name: z.string() }),
  scope: z.object({
    kind: z.enum(['site', 'region']),
    code: z.string(),
    name: z.string(),
  }),
  /** Every site in scope, so a region view can group rows. */
  sites: z.array(z.object({ code: z.string(), name: z.string() })),
  month: z.string(),
  today: IsoDate,
  days: z.array(CalendarDaySchema),
  /** Active staff in scope (after the role filter), family name order. */
  people: z.array(CalendarPersonSchema),
  /** Job titles present in scope, for the role filter. */
  roles: z.array(z.string()),
  entries: z.array(CalendarEntrySchema),
  bankHolidays: z.array(BankHolidaySchema),
  /** Site closures (M3 placeholder: always empty until closures are recorded). */
  closures: z.array(z.object({ date: IsoDate, siteCode: z.string(), name: z.string() })),
  /** Phase 2b M1: the absence layer (live absences of the people shown, type-coded). */
  absences: z.array(CalendarAbsenceEntrySchema),
  /**
   * True for the staff variant (own branch, the branch setting allows it): names only, approved
   * days only, no job titles; pending requests are not shown.
   */
  namesOnly: z.boolean(),
});
export type HolidayCalendarResponse = z.infer<typeof HolidayCalendarResponseSchema>;

// Import disagreements (D37) --------------------------------------------------------

export const HolidayDisagreementSchema = z.object({
  id: z.uuid(),
  personId: z.uuid(),
  name: z.string(),
  siteName: z.string().nullable(),
  year: z.number().int(),
  oldRemaining: z.number(),
  arooRemaining: z.number(),
  /** aroo − old */
  delta: z.number(),
  details: z.record(z.string(), z.unknown()),
  importRunId: z.string().nullable(),
  createdAt: z.iso.datetime(),
  /** D38: the only cause is Good Friday, which the old system never booked. */
  expected: z.boolean(),
  /** The import's reasons ("bank_holidays_added", "overlaps_skipped", "other"). */
  reasons: z.array(z.string()),
  /** M3: how HR settled it, if they have. */
  resolution: z.enum(['accepted_aroo', 'adjusted_to_old']).nullable(),
  resolvedAt: z.iso.datetime().nullable(),
  resolvedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export type HolidayDisagreement = z.infer<typeof HolidayDisagreementSchema>;

export const HolidayDisagreementsResponseSchema = z.object({
  year: z.number().int(),
  items: z.array(HolidayDisagreementSchema),
});
export type HolidayDisagreementsResponse = z.infer<typeof HolidayDisagreementsResponseSchema>;

export const HolidayDisagreementsQuerySchema = HolidayYearQuerySchema.extend({
  /** Include settled items (default: open only). */
  includeResolved: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type HolidayDisagreementsQuery = z.infer<typeof HolidayDisagreementsQuerySchema>;

// Phase 2a M2: requests, approvers and decisions ------------------------------------

/** Who may decide a request and how they got there (spec §3, D31). */
export const ApproverViaSchema = z.enum(['override', 'chain', 'cover', 'admin', 'hr']);
export type ApproverVia = z.infer<typeof ApproverViaSchema>;

export const ApproverSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  /** The seat that puts them in the set; null for an Aroo Admin with no seat in the chain. */
  positionId: z.uuid().nullable(),
  positionTitle: z.string().nullable(),
  via: ApproverViaSchema,
  /** 0 = the primary seat (override or line manager), 1 = the seat above it, and so on. */
  level: z.number().int().min(0),
});
export type Approver = z.infer<typeof ApproverSchema>;

/** GET /holidays/approvers/:personId */
export const ApproversResponseSchema = z.object({
  personId: z.uuid(),
  /** Shown as "Awaiting Sam Kaur (Branch Manager)"; null when nobody can approve yet. */
  primary: ApproverSchema.nullable(),
  approvers: z.array(ApproverSchema),
  /** The seat snapshotted on a new request (override, else the line-manager seat). */
  approverPositionId: z.uuid().nullable(),
  approverPositionTitle: z.string().nullable(),
});
export type ApproversResponse = z.infer<typeof ApproversResponseSchema>;

export const HolidayRequestCreateSchema = z.object({
  startDate: IsoDate,
  endDate: IsoDate,
  /** Single-day requests only. */
  half: HolidayHalfSchema.nullable().optional(),
  note: z.string().trim().max(500).optional(),
});
export type HolidayRequestCreate = z.infer<typeof HolidayRequestCreateSchema>;

/** POST /holidays/requests and POST /holidays/people/:id/book: the bookings created (one per calendar year). */
export const HolidayRequestResponseSchema = z.object({
  bookings: z.array(HolidayBookingSchema),
  /** Total days across the pieces. */
  days: Days,
  primary: ApproverSchema.nullable(),
});
export type HolidayRequestResponse = z.infer<typeof HolidayRequestResponseSchema>;

/** 422 bodies for a refused request or decision: the numbers, so the screen can say why. */
export const HolidayRuleErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  year: z.number().int().optional(),
  requested: z.number().optional(),
  remaining: z.number().optional(),
  entitled: z.number().optional(),
  taken: z.number().optional(),
  booked: z.number().optional(),
  pending: z.number().optional(),
  /** How many days short the request is. */
  short: z.number().optional(),
  /** The booking an overlap was found with. */
  clash: z
    .object({ startDate: IsoDate, endDate: IsoDate, status: HolidayBookingStatusSchema })
    .optional(),
});
export type HolidayRuleError = z.infer<typeof HolidayRuleErrorSchema>;

/** 409 when another approver got there first. */
export const AlreadyDecidedErrorSchema = z.object({
  error: z.literal('already_decided'),
  message: z.string(),
  status: HolidayBookingStatusSchema,
  decidedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
});
export type AlreadyDecidedError = z.infer<typeof AlreadyDecidedErrorSchema>;

export const ApprovalPersonSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  firstName: z.string(),
  initials: z.string(),
  jobTitle: z.string().nullable(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
});
export type ApprovalPerson = z.infer<typeof ApprovalPersonSchema>;

/** One pending request in the approvals list (oldest first). */
export const ApprovalItemSchema = z.object({
  id: z.uuid(),
  /** Set when a request was split across the year boundary: the pieces decide together. */
  groupId: z.uuid().nullable(),
  person: ApprovalPersonSchema,
  startDate: IsoDate,
  endDate: IsoDate,
  half: HolidayHalfSchema.nullable(),
  days: Days,
  note: z.string().nullable(),
  requestedAt: z.iso.datetime(),
  /** Whole days since it was requested. */
  waitingDays: z.number().int().min(0),
  /** Someone else at the site has a live booking on at least one of the days. */
  clash: z.boolean(),
  /** "Awaiting Sam Kaur (Branch Manager)". */
  primary: ApproverSchema.nullable(),
  imported: z.boolean(),
});
export type ApprovalItem = z.infer<typeof ApprovalItemSchema>;

export const ApprovalsResponseSchema = z.object({ items: z.array(ApprovalItemSchema) });
export type ApprovalsResponse = z.infer<typeof ApprovalsResponseSchema>;

/** Cell codes in the "Who's off that week" grid. */
export const CoverCellSchema = z.enum(['R', 'H', 'A', 'P', 'B', '.']);
export type CoverCell = z.infer<typeof CoverCellSchema>;

export const COVER_CELL_LABELS: Record<CoverCell, string> = {
  R: 'Off',
  H: 'Half',
  A: 'Off',
  P: 'Asked',
  B: 'BH',
  '.': 'In',
};

export const WhosOffSchema = z.object({
  columns: z.array(
    z.object({
      date: IsoDate,
      /** "Mon 19" */
      label: z.string(),
      bankHoliday: z.string().nullable(),
    }),
  ),
  rows: z.array(
    z.object({
      personId: z.uuid(),
      name: z.string(),
      jobTitle: z.string().nullable(),
      cells: z.array(CoverCellSchema),
    }),
  ),
  /** People at the site beyond the rows shown (the grid is capped). */
  more: z.number().int().min(0),
});
export type WhosOff = z.infer<typeof WhosOffSchema>;

/** GET /holidays/approvals/:id */
export const ApprovalDetailSchema = ApprovalItemSchema.extend({
  balance: z.object({
    year: z.number().int(),
    entitled: z.number(),
    /** Days left before this request is counted. */
    before: z.number(),
    /** Days left once it is approved. */
    after: z.number(),
    /** Other pending requests from the same person, in days. */
    otherPending: z.number(),
  }),
  whosOff: WhosOffSchema,
  /** Other people at the site with a live booking on the same days. */
  clashes: z.array(
    z.object({
      personId: z.uuid(),
      name: z.string(),
      startDate: IsoDate,
      endDate: IsoDate,
      status: z.enum(['pending', 'approved']),
    }),
  ),
  /** Minimum cover (spec §7 q3): a warning only, never a refusal. */
  cover: z.object({ kind: z.enum(['warn', 'ok']), text: z.string() }),
  events: z.array(HolidayBookingEventSchema),
  /** Other pieces of the same request (year split), already included in `days`. */
  siblings: z.array(z.object({ id: z.uuid(), startDate: IsoDate, endDate: IsoDate, days: Days })),
});
export type ApprovalDetail = z.infer<typeof ApprovalDetailSchema>;

export const ApproveRequestSchema = z.object({ note: z.string().trim().max(500).optional() });
export type ApproveRequest = z.infer<typeof ApproveRequestSchema>;

export const DeclineRequestSchema = z.object({
  note: z.string().trim().min(1, 'Give a reason').max(500),
});
export type DeclineRequest = z.infer<typeof DeclineRequestSchema>;

/** The booking after a decision, withdraw or cancel. */
export const HolidayDecisionResponseSchema = z.object({ bookings: z.array(HolidayBookingSchema) });
export type HolidayDecisionResponse = z.infer<typeof HolidayDecisionResponseSchema>;

export const BulkApproveRequestSchema = z.object({ ids: z.array(z.uuid()).min(1).max(50) });
export type BulkApproveRequest = z.infer<typeof BulkApproveRequestSchema>;

export const BulkApproveResponseSchema = z.object({
  results: z.array(
    z.object({
      id: z.uuid(),
      ok: z.boolean(),
      error: z.string().nullable(),
      message: z.string().nullable(),
    }),
  ),
});
export type BulkApproveResponse = z.infer<typeof BulkApproveResponseSchema>;

export const CancelBookingRequestSchema = z.object({
  reason: z.string().trim().min(1, 'Give a reason').max(500),
});
export type CancelBookingRequest = z.infer<typeof CancelBookingRequestSchema>;

/** GET /holidays/approvals/history: decided requests of the people the caller may approve for. */
export const ApprovalHistoryItemSchema = ApprovalItemSchema.extend({
  status: HolidayBookingStatusSchema,
  decidedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelReason: z.string().nullable(),
  /** Approved and not yet taken: the caller may cancel it from the list. */
  cancellable: z.boolean(),
});
export type ApprovalHistoryItem = z.infer<typeof ApprovalHistoryItemSchema>;

export const ApprovalHistoryResponseSchema = z.object({
  items: z.array(ApprovalHistoryItemSchema),
});
export type ApprovalHistoryResponse = z.infer<typeof ApprovalHistoryResponseSchema>;

// Phase 2a M3: HR holiday admin --------------------------------------------------------

export const HolidayAdminPeopleQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  /** Site code. */
  site: z.string().trim().max(32).optional(),
  /** Region code. */
  region: z.string().trim().max(32).optional(),
  /** Name or staff number contains. */
  q: z.string().trim().max(100).optional(),
});
export type HolidayAdminPeopleQuery = z.infer<typeof HolidayAdminPeopleQuerySchema>;

/** One row of HR › Holidays admin › People. */
export const HolidayAdminRowSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  firstName: z.string(),
  initials: z.string(),
  staffNumber: z.string().nullable(),
  jobTitle: z.string().nullable(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
  regionCode: z.string().nullable(),
  startDate: IsoDate.nullable(),
  /** Null when HR has not set this year yet. */
  allocation: Days.nullable(),
  adjustments: z.number(),
  taken: z.number(),
  booked: z.number(),
  pending: z.number(),
  remaining: z.number(),
  bankHolidaysBooked: z.boolean().nullable(),
  casual: z.boolean().nullable(),
  approverOverride: z.string().nullable(),
  /** "Mon–Sat" / "Wk1 Mon–Sat · Wk2 Mon–Fri" style summary of the pattern in force today. */
  patternSummary: z.string(),
  patternSource: WorkingPatternSourceSchema,
  /** aroo − old at import, when an open disagreement exists for the year. */
  disagreementDelta: z.number().nullable(),
  /** The spec's pro-rata suggestion for a starter whose start date is inside the year, else null. */
  proRataHint: z
    .object({ startDate: IsoDate, daysRemaining: z.number().int(), daysInYear: z.number().int() })
    .nullable(),
});
export type HolidayAdminRow = z.infer<typeof HolidayAdminRowSchema>;

export const HolidayAdminPeopleResponseSchema = z.object({
  year: z.number().int(),
  today: IsoDate,
  items: z.array(HolidayAdminRowSchema),
});
export type HolidayAdminPeopleResponse = z.infer<typeof HolidayAdminPeopleResponseSchema>;

/** PUT /holidays/admin/people/:id/year/:year */
export const HolidayYearPutSchema = z.object({
  allocationDays: Days.min(0).max(100),
  bankHolidaysBooked: z.boolean(),
  casual: z.boolean(),
  approverPositionOverrideId: z.uuid().nullable(),
});
export type HolidayYearPut = z.infer<typeof HolidayYearPutSchema>;

export const ProRataSuggestionSchema = z.object({
  /** ceil(allocation × days remaining ÷ days in year), capped (spec §1). */
  suggested: Days,
  /** The full-year allocation the suggestion was computed from. */
  basedOn: Days,
  startDate: IsoDate,
  daysRemaining: z.number().int(),
  daysInYear: z.number().int(),
});
export type ProRataSuggestion = z.infer<typeof ProRataSuggestionSchema>;

/** GET and PUT /holidays/admin/people/:id/year/:year */
export const HolidayAdminYearSchema = z.object({
  summary: HolidaySummarySchema,
  startDate: IsoDate.nullable(),
  approverPositionOverride: z
    .object({ id: z.uuid(), code: z.string(), title: z.string() })
    .nullable(),
  /** Set for a starter whose start date falls inside the year (from the allocation on record or
   *  the one just submitted); null otherwise. */
  proRata: ProRataSuggestionSchema.nullable(),
  /** Bank-holiday bookings the PUT created or removed (dates); empty on a GET. */
  autoBookings: z.object({ created: z.array(IsoDate), removed: z.array(IsoDate) }),
});
export type HolidayAdminYear = z.infer<typeof HolidayAdminYearSchema>;

export const HolidayAdjustmentCreateSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  /** Signed, in halves, never zero, at most 60 either way. */
  days: Days.refine((d) => d !== 0, 'Enter the days to add or take away')
    .min(-60)
    .max(60),
  kind: HolidayAdjustmentKindSchema,
  reason: z.string().trim().min(1, 'Give a reason').max(500),
});
export type HolidayAdjustmentCreate = z.infer<typeof HolidayAdjustmentCreateSchema>;

export const HolidayAdjustmentResponseSchema = z.object({
  adjustment: HolidayAdjustmentSchema,
  balance: HolidayBalanceSchema,
});
export type HolidayAdjustmentResponse = z.infer<typeof HolidayAdjustmentResponseSchema>;

export const DisagreementActionResponseSchema = z.object({
  disagreement: HolidayDisagreementSchema,
  /** The correction written by "Adjust to old figure"; null for "Accept". */
  adjustment: HolidayAdjustmentSchema.nullable(),
});
export type DisagreementActionResponse = z.infer<typeof DisagreementActionResponseSchema>;

// Per-site settings ----------------------------------------------------------------------

export const SiteHolidaySettingsSchema = z.object({
  site: z.object({ code: z.string(), name: z.string(), format: z.string() }),
  staffSeeBranchCalendar: z.boolean(),
  tradingDays: WeeklyPatternSchema,
  saturdayCycleAnchor: IsoDate.nullable(),
});
export type SiteHolidaySettings = z.infer<typeof SiteHolidaySettingsSchema>;

export const SiteHolidaySettingsPutSchema = z.object({
  staffSeeBranchCalendar: z.boolean(),
  tradingDays: WeeklyPatternSchema,
});
export type SiteHolidaySettingsPut = z.infer<typeof SiteHolidaySettingsPutSchema>;

// Year-end tool (Phase 2a M4, spec §4 screen 4) -------------------------------------------

const Year = z.coerce.number().int().min(2000).max(2100);

/** GET /holidays/admin/year-end?from=&to= (to defaults to from + 1). */
export const YearEndQuerySchema = z.object({
  from: Year.optional(),
  to: Year.optional(),
});
export type YearEndQuery = z.infer<typeof YearEndQuerySchema>;

export const YearEndRowSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  staffNumber: z.string().nullable(),
  jobTitle: z.string().nullable(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
  regionCode: z.string().nullable(),
  startDate: IsoDate.nullable(),
  /** A leaving date already recorded (the row is unticked by default). */
  leavingOn: IsoDate.nullable(),
  /** The `from` year as it stands. */
  allocation: Days.nullable(),
  adjustments: z.number(),
  taken: z.number(),
  booked: z.number(),
  pending: z.number(),
  remaining: z.number(),
  bankHolidaysBooked: z.boolean().nullable(),
  casual: z.boolean().nullable(),
  /** The `to` year, when HR (or a previous run) already created it. */
  toYearExists: z.boolean(),
  toAllocation: Days.nullable(),
  toBankHolidaysBooked: z.boolean().nullable(),
  toCasual: z.boolean().nullable(),
  /** The live carry-over adjustment written for `to` by a previous run (0 when none). */
  carryOverApplied: Days,
  /** Next year's allocation suggestion: this year's, pro-rata when the start date is in `to`. */
  suggestedAllocation: Days.nullable(),
  /** The start date is inside `from`: this year's allocation was probably pro-rata. */
  starter: z.boolean(),
});
export type YearEndRow = z.infer<typeof YearEndRowSchema>;

export const YearEndResponseSchema = z.object({
  from: z.number().int(),
  to: z.number().int(),
  today: IsoDate,
  /** Bank holidays for `to` are in the table (the weekly job fetched them). */
  toBankHolidaysKnown: z.boolean(),
  items: z.array(YearEndRowSchema),
});
export type YearEndResponse = z.infer<typeof YearEndResponseSchema>;

export const YearEndApplyRowSchema = z.object({
  personId: z.uuid(),
  allocationDays: Days.min(0).max(100),
  /** Signed; 0 means "no carry-over" (a previous one is reversed). */
  carryOverDays: Days.min(-60).max(60),
  bankHolidaysBooked: z.boolean(),
  casual: z.boolean(),
});
export type YearEndApplyRow = z.infer<typeof YearEndApplyRowSchema>;

/** POST /holidays/admin/year-end/apply */
export const YearEndApplySchema = z.object({
  from: Year,
  to: Year,
  rows: z.array(YearEndApplyRowSchema).min(1).max(2000),
});
export type YearEndApply = z.infer<typeof YearEndApplySchema>;

export const YearEndApplyResultSchema = z.object({
  personId: z.uuid(),
  ok: z.boolean(),
  error: z.string().nullable(),
  /** The `to` year row was created (false: updated). */
  created: z.boolean(),
  /** The carry-over adjustment was written, replaced or reversed. */
  carryOverChanged: z.boolean(),
  bankHolidaysCreated: z.number().int(),
  bankHolidaysRemoved: z.number().int(),
});
export type YearEndApplyResult = z.infer<typeof YearEndApplyResultSchema>;

export const YearEndApplyResponseSchema = z.object({
  from: z.number().int(),
  to: z.number().int(),
  applied: z.number().int(),
  failed: z.number().int(),
  results: z.array(YearEndApplyResultSchema),
});
export type YearEndApplyResponse = z.infer<typeof YearEndApplyResponseSchema>;

// Reports (Phase 2a M4, spec §4 screen 5) ----------------------------------------------------

/** Every report takes the year and an optional site or region code within the caller's scope. */
export const HolidayReportQuerySchema = z.object({
  year: Year.optional(),
  site: z.string().trim().max(32).optional(),
  region: z.string().trim().max(32).optional(),
  format: z.enum(['json', 'csv']).optional(),
});
export type HolidayReportQuery = z.infer<typeof HolidayReportQuerySchema>;

export const NothingBookedQuerySchema = HolidayReportQuerySchema.extend({
  /** People with at least this many days remaining (default 10). */
  minDays: z.coerce.number().min(0).max(100).optional(),
  /** Nothing approved starting after this date (default 1 September of the year). */
  asOf: IsoDate.optional(),
});
export type NothingBookedQuery = z.infer<typeof NothingBookedQuerySchema>;

export const ReportScopeSchema = z.object({
  kind: z.enum(['company', 'region', 'site']),
  code: z.string().nullable(),
  name: z.string(),
});
export type ReportScope = z.infer<typeof ReportScopeSchema>;

export const ReportPersonSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  staffNumber: z.string().nullable(),
  jobTitle: z.string().nullable(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
  regionCode: z.string().nullable(),
  casual: z.boolean(),
});
export type ReportPerson = z.infer<typeof ReportPersonSchema>;

export const BalanceReportRowSchema = ReportPersonSchema.extend({
  allocation: Days.nullable(),
  entitled: z.number(),
  taken: z.number(),
  booked: z.number(),
  pending: z.number(),
  remaining: z.number(),
});
export type BalanceReportRow = z.infer<typeof BalanceReportRowSchema>;

export const BalanceTotalsSchema = z.object({
  people: z.number().int(),
  entitled: z.number(),
  taken: z.number(),
  booked: z.number(),
  pending: z.number(),
  remaining: z.number(),
});
export type BalanceTotals = z.infer<typeof BalanceTotalsSchema>;

export const BalancesReportSchema = z.object({
  year: z.number().int(),
  today: IsoDate,
  scope: ReportScopeSchema,
  items: z.array(BalanceReportRowSchema),
  totals: BalanceTotalsSchema,
});
export type BalancesReport = z.infer<typeof BalancesReportSchema>;

export const LiabilityRowSchema = BalanceTotalsSchema.extend({
  site: z.object({ code: z.string(), name: z.string() }),
  region: z.object({ code: z.string(), name: z.string() }).nullable(),
});
export type LiabilityRow = z.infer<typeof LiabilityRowSchema>;

export const LiabilityReportSchema = z.object({
  year: z.number().int(),
  today: IsoDate,
  scope: ReportScopeSchema,
  /** Per site, remaining days as of today; pay-weighted liability arrives in Phase 3. */
  sites: z.array(LiabilityRowSchema),
  regions: z.array(
    BalanceTotalsSchema.extend({ region: z.object({ code: z.string(), name: z.string() }) }),
  ),
  totals: BalanceTotalsSchema,
  note: z.string(),
});
export type LiabilityReport = z.infer<typeof LiabilityReportSchema>;

export const BookingsByMonthReportSchema = z.object({
  year: z.number().int(),
  scope: ReportScopeSchema,
  sites: z.array(z.object({ code: z.string(), name: z.string() })),
  /** Twelve rows, January first: approved holiday days by the month the booking starts in. */
  months: z.array(
    z.object({
      month: z.string(),
      label: z.string(),
      total: z.number(),
      bookings: z.number().int(),
      bySite: z.record(z.string(), z.number()),
    }),
  ),
  total: z.number(),
});
export type BookingsByMonthReport = z.infer<typeof BookingsByMonthReportSchema>;

export const NothingBookedRowSchema = ReportPersonSchema.extend({
  remaining: z.number(),
  /** The last approved holiday on record for the year, if any. */
  lastBooked: IsoDate.nullable(),
});
export type NothingBookedRow = z.infer<typeof NothingBookedRowSchema>;

export const NothingBookedReportSchema = z.object({
  year: z.number().int(),
  today: IsoDate,
  scope: ReportScopeSchema,
  minDays: z.number(),
  asOf: IsoDate,
  items: z.array(NothingBookedRowSchema),
});
export type NothingBookedReport = z.infer<typeof NothingBookedReportSchema>;

export const HolidayExceptionKindSchema = z.enum([
  'disagreement',
  'negative_balance',
  'no_holiday_year',
  'pattern_missing',
]);
export type HolidayExceptionKind = z.infer<typeof HolidayExceptionKindSchema>;

export const HOLIDAY_EXCEPTION_LABELS: Record<HolidayExceptionKind, string> = {
  disagreement: 'Disagrees with the old system',
  negative_balance: 'Over their balance',
  no_holiday_year: 'No holiday year',
  pattern_missing: 'No working pattern',
};

export const HolidayExceptionRowSchema = ReportPersonSchema.extend({
  kind: HolidayExceptionKindSchema,
  detail: z.string(),
  /** The figure behind it: the delta, the negative remaining; null for the flags. */
  value: z.number().nullable(),
});
export type HolidayExceptionRow = z.infer<typeof HolidayExceptionRowSchema>;

export const ExceptionsReportSchema = z.object({
  year: z.number().int(),
  today: IsoDate,
  scope: ReportScopeSchema,
  items: z.array(HolidayExceptionRowSchema),
  counts: z.record(HolidayExceptionKindSchema, z.number().int()),
});
export type ExceptionsReport = z.infer<typeof ExceptionsReportSchema>;

// Per-person preferences (the daily digest opt-out) ---------------------------------------

export const HolidayPrefsSchema = z.object({
  dailyDigestOptOut: z.boolean(),
});
export type HolidayPrefs = z.infer<typeof HolidayPrefsSchema>;

export const HolidayPrefsPatchSchema = z.object({
  dailyDigestOptOut: z.boolean().optional(),
});
export type HolidayPrefsPatch = z.infer<typeof HolidayPrefsPatchSchema>;
